import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { History } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { ImportRunsTable } from '@/components/publications/importacoes/ImportRunsTable'
import { ImportNowDialog } from '@/components/publications/importacoes/ImportNowDialog'
import { useAuth } from '@/contexts/AuthContext'
import { isSocio } from '@/lib/roles'
import { ImportRun, importacoesService, Monitor } from '@/services/importacoes-service'
import { apiErrorMessage } from '@/services/team-service'

const POLL_MS = 5000

/** Publicações › Importações: histórico das consultas ao DJEN e "Importar agora" (sócio). */
export function PublicationImportsPage() {
  const { user } = useAuth()
  const canImport = isSocio(user?.role)
  const [runs, setRuns] = useState<ImportRun[]>([])
  const [monitors, setMonitors] = useState<Monitor[]>([])
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  const load = useCallback(async () => {
    try {
      const [loadedRuns, loadedMonitors] = await Promise.all([importacoesService.listRuns(), importacoesService.listMonitors()])
      setRuns(loadedRuns)
      setMonitors(loadedMonitors)
      setError(null)
    } catch (err) {
      setError(apiErrorMessage(err, 'Não foi possível carregar as importações'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  // Enquanto houver execução em andamento, atualiza a lista.
  const running = runs.some((run) => run.status === 'EM_ANDAMENTO')
  useEffect(() => {
    if (!running) return
    const timer = setInterval(load, POLL_MS)
    return () => clearInterval(timer)
  }, [running, load])

  return (
    <div className="flex flex-col gap-4 p-4 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <History className="size-6 text-muted-foreground" />
          <h1 className="text-2xl font-semibold">Importações</h1>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" asChild><Link to="/publications">Caixa de entrada</Link></Button>
          <Button variant="outline" asChild><Link to="/publications/monitorados">Monitorados</Link></Button>
          {canImport && <ImportNowDialog monitors={monitors} onStarted={load} />}
        </div>
      </div>
      {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
      <Card>
        <CardHeader>
          <CardTitle>Execuções</CardTitle>
          <CardDescription>
            Automáticas às 7h, 11h, 15h e 19h. Cada monitoramento tem até 3 tentativas; 2 falhas seguidas avisam no sino.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? <p className="text-sm text-muted-foreground">Carregando…</p> : <ImportRunsTable runs={runs} />}
        </CardContent>
      </Card>
    </div>
  )
}
